
import { Component, Prop, Vue } from 'vue-property-decorator';
import { container } from '../lib/container';
import { Actions, ActionStates } from '../lib/Actions';
import { ContainerTypes } from '../lib/core/types/ContainerTypes';
import { isResultEntry, isSearchEntries, isRouteEntry } from '../lib/core/guards';
import { SearchStorage } from '../lib/core/storage/SearchStorage';
import { setCmd, execCmd } from '../lib/core/storage/CommandStorage';
import { DetailsStorage } from '../lib/core/storage/DetailsStorage';
import { RoutingStorage } from '../lib/core/storage/RoutingStorage';
import { getConfig } from '../lib/ConfigLoader';
import { LatLngBounds } from 'leaflet';
import ResizeObserver from 'resize-observer-polyfill';
import { formatAddressLine } from '../lib/core/util';
import { AppBehavior } from '../lib/core/AppBehavior';
import { onlyDevice, notDevice, ltTabletDevice } from '../lib/core/responsive';
import { MutationTypes } from '../store/mutation-types';

type Polyline = {
	addTo(layer: unknown): void;
	getBounds(): ym.provider.LatLngBoundsContext;
};

const actions = container.get<Actions>(ContainerTypes.Action);
const searchStorage = container.get<SearchStorage>(ContainerTypes.SearchStorage);
const detailsStorage = container.get<DetailsStorage>(ContainerTypes.DetailsStorage);
const routingStorage = container.get<RoutingStorage>(ContainerTypes.RoutingStorage);
const behavior = container.get<AppBehavior>('behavior');

actions.on(ActionStates.Idle, (from, event) => execCmd('mapIdle', from, event));
actions.on(ActionStates.UpdateMap, (from, event) => execCmd('updateMap', from, event));
actions.on(ActionStates.HomePage, () => execCmd('resetToDefault'));

@Component
export default class MapView extends Vue {
	@Prop({ required: true })
	provider!: any;
	@Prop({ required: true })
	lat!: number | string;
	@Prop({ required: true })
	lng!: number | string;
	@Prop({ required: true })
	zoom!: number | string;
	@Prop({ required: true })
	bbox!: string;

	map!: ym.provider.MapContext;
	geoLayer!: ym.provider.LayerGroupContext;
	layers!: ym.provider.LayerGroupContext;
	currentMarker!: any;
	lastPosition!: any;

	highlightDetailsItem = (provider: any, id: string) => (layer: any) => {
		const icon = layer.options.icon;
		if (icon.options.html) {
			if (layer.options.id === id) {
				icon.options.html = layer.options.template(true, false);
				layer.setIcon(new provider.DivIcon(icon.options));
				layer.setOpacity(1);
				layer.setZIndexOffset(200);
				this.currentMarker = layer;
			} else {
				icon.options.html = layer.options.template(false, false);
				layer.setIcon(new provider.DivIcon(icon.options));
				layer.setOpacity(0.5);
				layer.setZIndexOffset(100);
			}
		}
	};

	getLatLng(position: ym.provider.LatLngLiteral) {
		const { provider } = this.$props;
		return new provider.LatLng(position.lat, position.lng);
	}

	fitBounds(bounds: LatLngBounds) {
		const { provider } = this.$props;
		this.map.invalidateSize();
		this.map.fitBounds(new provider.LatLngBounds(bounds.getNorthEast(), bounds.getSouthWest()));
	}

	setView(latlng: ym.provider.LatLngLiteral, zoom: number): void {
		this.map.invalidateSize();
		this.map.setView(latlng, zoom);
	}

	panTo(event: { position: ym.provider.LatLngLiteral }) {
		if (!event || !event.position) {
			return;
		}
		const latLng = new this.$props.provider.LatLng(event.position.lat, event.position.lng);
		const detailsZoom = 16;
		const currentZoom = this.map.getZoom();
		const zoom = currentZoom >= detailsZoom ? currentZoom : detailsZoom;
		this.setView(latLng, zoom);
		this.currentMarker = null;
	}

	setMarker(iconData: any, item: any, latlng: any, map: any, provider: any) {
		const config = getConfig();
		const activeIcon = iconData.highlightMarker.activeIcon || config.defaultMarkerSize.activeIcon;
		const inactiveIcon = iconData.highlightMarker.inactiveIcon || config.defaultMarkerSize.inactiveIcon;
		const iconAnchor = iconData.highlightMarker.iconAnchor || config.defaultMarkerSize.iconAnchor;
		const sliderWidth = iconData.highlightMarker.sliderWidth || config.defaultMarkerSize.sliderWidth;

		const src = iconData.highlightMarker.src;

		let contentOffset = '';
		if (iconData.contentOffset) {
			if (iconData.contentOffset.top > 0) {
				contentOffset += ' content-offset-top="' + iconData.contentOffset.top + '"';
			}
			if (iconData.contentOffset.bottom > 0) {
				contentOffset += ' content-offset-bottom="' + iconData.contentOffset.bottom + '"';
			}
		}

		const template = (active = false, sliderEnabled = true) =>
			`<highlight-marker active="${active}" slider-enabled="${sliderEnabled}" active-icon="${activeIcon.join(
				'x'
			)}" inactive-icon="${inactiveIcon.join('x')}" slider-width="${sliderWidth}" src="${src.replace(
				/"/g,
				'&quot;'
			)}" headline="${(item.address.name || '').replace(
				/"/g,
				'&quot;'
			)}"${contentOffset} content="${formatAddressLine(item.address).replace('"', '&quot;')}"${
				item.ekomiId ? ` ekomi-id="${item.ekomiId}"` : ''
			}></highlight-marker>`;

		const iconSizePt = new provider.Point(...activeIcon);
		const iconAnchorPt = new provider.Point(...iconAnchor);

		return new provider.Marker(latlng, {
			id: item.idDecoded,
			template: template,
			zIndexOffset: 200,
			icon: new provider.DivIcon({
				html: template(false, true),
				className: 'ym-marker-container',
				iconSize: iconSizePt,
				iconAnchor: iconAnchorPt
			})
		}).addTo(map);
	}

	mounted() {
		this.$nextTick(() => {
			const { provider, lat, lng } = this.$props;
			const Map: ym.provider.Map = provider.Map;

			const isSmallDevice = window.innerWidth <= 350;

			const mapOptions = {
				center: { lat, lng },
				brandControl: !behavior.appView,
				zoomControl: false,
				attributionControl: !isSmallDevice
			};

			const map = (this.map = new Map(this.$el as HTMLElement, mapOptions));
			const controls = (ym as any).modules.provider.Control;

			if (behavior.appView) {
				const brandControl = new controls.Brand({
					position: 'bottomleft',
					logo: ym.options.logo,
					link: ym.options.link
				});
				map.addControl(brandControl);

				if (isSmallDevice) {
					const attrControl = new controls.Attribution({
						position: 'bottomright',
						prefix: 'Leaflet'
					});
					map.addControl(attrControl);
				}
			}

			const zoomControl = new controls.Zoom({ position: 'topright' });
			map.addControl(zoomControl);

			var resizeObserver = new ResizeObserver(() => map.invalidateSize());
			resizeObserver.observe(this.$el);

			// if (this.bbox) {
			// 	const [minLng, minLat, maxLng, maxLat] = this.bbox.split(',').map(n => parseFloat(n));
			// 	if (undefined !== minLng && undefined !== minLat && undefined !== maxLng && undefined !== maxLat) {
			// 		this.fitBounds(new provider.LatLngBounds([minLat, minLng], [maxLat, maxLng]));
			// 	}
			// }

			this.geoLayer = new provider.LayerGroup();
			this.geoLayer.addTo(map);

			this.layers = new provider.LayerGroup();
			this.layers.addTo(map);

			let activeId: string;
			setCmd('mapIdle', (from, event) => {
				activeId = '';
				this.lastPosition = null;
			});

			setCmd('resetToDefault', () => this.resetToMapDefaults());

			setCmd('updateMap', (from, event) => {
				const config = getConfig();
				let bounds = new provider.LatLngBounds();
				this.geoLayer.clearLayers();

				if ('updateResults' === event.type && isSearchEntries(searchStorage.results)) {
					this.layers.clearLayers();

					const searchIcon = getConfig().searchIcon;

					if (searchIcon) {
						new provider.Marker(searchStorage.searchCenter, {
							zIndexOffset: 100,
							icon: new provider.Icon(searchIcon)
						}).addTo(this.layers);
					} else {
						new provider.CircleMarker(searchStorage.searchCenter, {
							zIndexOffset: 100,
							fillColor: '#2743a0',
							color: '#2743a0'
						}).addTo(this.layers);
					}

					bounds.extend(searchStorage.searchCenter);

					if (searchStorage.results.entries.length > 0) {
						for (const item of searchStorage.results.entries) {
							const latlng = this.getLatLng(item.position);
							let marker: any;

							if (isResultEntry(item)) {
								mainLoop: for (const iconData of config.icons) {
									if (!iconData.branches || !iconData.branches.length) {
										marker = this.setMarker(iconData, item, latlng, map, provider);
										// (ym as any).circleMarker(latlng, { radius: 3 }).addTo(map);
										continue;
									}
									for (const iconBC of iconData.branches) {
										for (const bc of item.branchCodes) {
											if (iconBC === bc) {
												marker = this.setMarker(iconData, item, latlng, map, provider);
												// (ym as any).circleMarker(latlng, { radius: 3 }).addTo(map);
												break mainLoop;
											}
										}
									}
								}
								if (!marker) {
									marker = new provider.Marker(latlng, {
										zIndexOffset: 200
									});
								}

								marker.on('click', () => {
									if (isResultEntry(item)) {
										if (activeId === item.idDecoded) {
											return;
										} else if (!activeId) {
											activeId = item.idDecoded;
										}
									}

									this.currentMarker = marker;

									// map.setView(marker.getLatLng(), map.getZoom());
									actions.go(ActionStates.CallDetails, {
										from: 'marker',
										id: item.idDecoded,
										position: item.position,
										isMarkerClick: true
									});
									this.$store.commit(MutationTypes.detailViewSelectMarker, {
										selectedMarker: item.idDecoded,
										mode: 'preview'
									});
								});
							}

							bounds.extend(latlng.original);
							this.layers.addLayer(marker);
						}
					}

					if (searchStorage.results.fitBounds || event.fitBounds) {
						if (!bounds.isValid()) {
							bounds = map.getBounds() as ym.provider.LatLngBoundsContext & {
								original: any;
							};
						}
						if (searchStorage.results.entries.length > 0) {
							this.fitBounds(bounds);
						} else {
							this.setView(searchStorage.searchCenter, 12);
						}
						searchStorage.setMap(map);
					} else {
						actions.go(ActionStates.Idle, {});
					}
				} else if ('updateDetails' === event.type && isResultEntry(detailsStorage.entry)) {
					activeId = detailsStorage.entry.idDecoded;

					if (event !== undefined && event.pan) {
						if (from === ActionStates.UpdateDetails) {
							const layerIteratee = this.highlightDetailsItem(provider, detailsStorage.entry.idDecoded);

							// highlight the currently active item in the details
							// view and decrease visibility of every other item on the map
							this.layers.eachLayer(layerIteratee);
							actions.go(ActionStates.Lock, event);

							// must be called after Lock!
							this.panTo(event);
						} else {
							this.layers.eachLayer((layer: any) => {
								const icon = layer.options.icon;
								if (icon.html) {
									icon.html = layer.options.template(false, true);
									layer.setIcon(new provider.DivIcon(icon));
								}
							});

							map.setView(searchStorage.map.position, searchStorage.map.zoom);
						}
					}
				} else if ('updateRouting' === event.type && isResultEntry(routingStorage.entry)) {
					if (from === ActionStates.UpdateRouting && isRouteEntry(routingStorage.routeEntry)) {
						const servicePolyline: ym.provider.LatLngLiteral[] = (
							(routingStorage.routeEntry.polyline as Array<any>) || []
						).map<ym.provider.LatLngLiteral>(([lat, lng]) => ({ lat, lng }));

						const polyline: Polyline = new provider.Polyline(servicePolyline, {
							weight: 8,
							opacity: 0.8
						});
						polyline.addTo(this.geoLayer);

						for (const waypoint of routingStorage.routeEntry.waypoints || []) {
							const marker = new provider.CircleMarker(waypoint.position, {
								zIndexOffset: 200,
								radius: 7,
								fillOpacity: 0.8,
								fillColor: '#ffffff',
								weight: 1
							});
							marker.bindPopup(waypoint.description);
							marker.on('mouseover', () => {
								marker.openPopup();
							});
							marker.on('mouseout', () => {
								marker.closePopup();
							});
							marker.addTo(this.geoLayer);
						}

						const endWaypointCenter = servicePolyline[servicePolyline.length - 1];
						if (endWaypointCenter) {
							const endWaypoint = new provider.CircleMarker(servicePolyline[servicePolyline.length - 1], {
								zIndexOffset: 200,
								radius: 7,
								fillOpacity: 0.8,
								fillColor: '#ffffff',
								weight: 1
							});

							endWaypoint.bindPopup('Sie haben Ihr Ziel erreicht.');
							endWaypoint.on('mouseover', () => {
								endWaypoint.openPopup();
							});
							endWaypoint.on('mouseout', () => {
								endWaypoint.closePopup();
							});

							endWaypoint.addTo(this.geoLayer);
						}

						bounds = polyline.getBounds();
						if (bounds.isValid()) {
							this.fitBounds(bounds);
						}
					}
					actions.go(ActionStates.Lock, event);
				}
			});

			map.whenReady(() => {
				let isRealUserDragging = false;
				map.on('movestart', () => {
					isRealUserDragging = true;
				});

				if (this.$store.state.isAppView && ltTabletDevice()) {
					map.on('click', () => {
						const { offset } = detailsStorage;
						actions.go(ActionStates.CallResults, {
							offset,
							pan: true,
							fitBounds: true
						});
						this.$store.commit(MutationTypes.detailViewSelectMarker, { selectedMarker: null });
					});
				}

				const onMoveEnd = (type: string) => {
					if (isRealUserDragging) {
						isRealUserDragging = false;
						if (actions.canGo(ActionStates.SearchMap)) {
							searchStorage.setMap(map);
							actions.go(ActionStates.SearchMap, { type: 'Rectangle' });
						} else if (actions.canGo(ActionStates.Idle)) {
							actions.go(ActionStates.Idle, {});
						}
					} else if (actions.canGo(ActionStates.Idle)) {
						actions.go(ActionStates.Idle, {});
					}
				};

				if (ym.options.engine === 'google') {
					map.on('zoomend', () => onMoveEnd('zoomend'));
				}
				map.on('moveend', () => onMoveEnd('moveend'));
				actions.go(ActionStates.Initialize, { map, provider });
			});
		});
	}

	private resetToMapDefaults(): void {
		const { lat, lng, zoom } = this.$props;
		console.debug('reset map to defaults, ', { lat, lng, zoom });
		this.setView({ lat, lng }, zoom);

		this.layers.clearLayers();
	}
}
